<template>
  <new-modal-dialog
    scrollable
    max-width="900px"
    :title="title || $t('label.preview')"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @close="$emit('close')"
  >
    <div
      v-if="loading"
      class="d-flex justify-center align-center py-12"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <v-alert
      v-else-if="error"
      type="error"
      text
      class="ma-0"
    >
      {{ error }}
    </v-alert>

    <template v-else-if="preview">
      <email-preview-frame
        v-if="isEmail"
        :subject="preview.subject"
      >
        <iframe
          class="preview-iframe d-block w-100 white"
          sandbox=""
          :title="String($t('label.preview'))"
          :srcdoc="preview.html"
        />
      </email-preview-frame>

      <div
        v-else
        class="d-flex justify-center py-4"
      >
        <div
          class="sms-bubble px-4 py-3 rounded-lg w-100 text-pre-wrap blue lighten-5 black--text"
        >
          {{ preview.plaintext }}
        </div>
      </div>
    </template>

    <template #actions>
      <v-spacer />
      <v-btn
        color="primary"
        text
        @click="$emit('close')"
      >
        {{ $t('button.close') }}
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import EmailPreviewFrame from '@/components/views/newsletter/EmailPreviewFrame.vue';
import store from '@/store/store';
import { TemplatePreview } from '@/grpc-api/grpc-client';

// Render email (iframe) vs plaintext by message type id, mirroring the backend's own gate
// (messageTypeEmail = 1): the backend produces HTML with a layout only for type 1, so keying
// off the id keeps this modal consistent with exactly what the backend rendered (rather than
// the webapp's separate channel mapping, which could diverge from the backend's gate).
const EMAIL_MESSAGE_TYPE_ID = 1;

const props = defineProps({
  messageTypeId: { type: Number, required: true },
  eventTypeId: { type: Number, required: true },
  locale: { type: String, required: false, default: '' },
  subject: { type: String, required: false, default: '' },
  body: { type: String, required: false, default: '' },
  title: { type: String, required: false, default: '' },
  serviceId: { type: Number, required: false, default: 0 },
});

defineEmits<{(e: 'close'): void}>();

const isEmail = computed(() => props.messageTypeId === EMAIL_MESSAGE_TYPE_ID);

const loading = ref(true);
const error = ref('');
const preview = ref<TemplatePreview | null>(null);

onMounted(async () => {
  try {
    preview.value = await store.dispatch('messageCenterPreviewTemplate', {
      messageTypeId: props.messageTypeId,
      eventTypeId: props.eventTypeId,
      locale: props.locale,
      body: props.body,
      subject: props.subject,
      serviceId: props.serviceId,
    });
  } catch (e: any) {
    error.value = e?.message ?? String(e);
  } finally {
    loading.value = false;
  }
});
</script>

<style lang="scss" scoped>
// single scroll region (mail-client model): the dialog chrome (title, macOS bar,
// subject, actions) stays fixed and only the email scrolls inside the iframe.
// The iframe is sized to the dialog's available height (90vh dialog minus ~270px
// chrome) so the scrollable v-card-text never overflows into a second scrollbar.
.preview-iframe {
  height: calc(90vh - 270px);
  min-height: 320px;
  border: 0;
}

// the shared EmailPreviewFrame caps its body at 600px with its own overflow;
// neutralize that here (newsletter usage keeps it) - the iframe handles scrolling
::v-deep .email-body-container {
  max-height: none;
  overflow: visible;
}

.sms-bubble {
  max-width: 420px;
  word-break: break-word;
  line-height: 1.4;
}
</style>
